import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, TrendingUp, DollarSign, Target, Calendar, ChevronRight, ChevronDown,
    Image as ImageIcon, Play, Sparkles, Eye, MousePointer, BarChart3,
    AlertTriangle, CheckCircle, Pause, Layers
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import {
    formatNumber, formatCurrency, formatPercent,
    DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRange
} from '../utils/helpers';
import CampaignCreativeRow from '../components/CampaignCreativeRow';

const PLATFORMS = [
    { id: 'meta', label: 'Meta', icon: '📘' },
    { id: 'google', label: 'Google', icon: '🔍' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
    { id: 'amazon', label: 'Amazon', icon: '📦' }
];

const CampaignCreatives = () => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();

    // State
    const [campaigns, setCampaigns] = useState([]);
    const [platformConnections, setPlatformConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlatform, setSelectedPlatform] = useState('meta');
    const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGE_PRESETS.LAST_30_DAYS);
    const [dateRange, setDateRange] = useState(() => getDateRange(DATE_RANGE_PRESETS.LAST_30_DAYS));
    const [expandedCampaignId, setExpandedCampaignId] = useState(null);

    // Analysis cache state
    const [creativeAnalysisCache, setCreativeAnalysisCache] = useState({});
    const [campaignAnalysisCache, setCampaignAnalysisCache] = useState({});

    // Load cached analysis on mount
    useEffect(() => {
        if (!selectedBrand) return;
        try {
            const creativeCache = localStorage.getItem(`campaign-creative-analysis-${selectedBrand.id}`);
            if (creativeCache) {
                const { data, timestamp } = JSON.parse(creativeCache);
                if (Date.now() - timestamp < 24 * 3600000) {
                    setCreativeAnalysisCache(data);
                }
            }
            const campaignCache = localStorage.getItem(`campaign-analysis-${selectedBrand.id}`);
            if (campaignCache) {
                const { data, timestamp } = JSON.parse(campaignCache);
                if (Date.now() - timestamp < 24 * 3600000) {
                    setCampaignAnalysisCache(data);
                }
            }
        } catch (e) {
            console.warn('Failed to load analysis cache:', e);
        }
    }, [selectedBrand]);

    // Real-time Firestore subscription to campaigns
    useEffect(() => {
        if (!selectedBrand || !currentUser) {
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, 'campaigns'),
            where('brandId', '==', selectedBrand.id),
            where('userId', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => (b.currentMetrics?.spend || 0) - (a.currentMetrics?.spend || 0));
            setCampaigns(data);
            setLoading(false);
        }, (error) => {
            console.error('Firestore query error:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [selectedBrand, currentUser]);

    // Fetch platform connections
    const fetchConnections = useCallback(async () => {
        if (!selectedBrand || !currentUser) return;
        try {
            const response = await fetch(
                `http://localhost:3001/api/campaigns/connections?userId=${currentUser.uid}&brandId=${selectedBrand.id}`
            );
            const data = await response.json();
            setPlatformConnections(data.connections || []);
        } catch (error) {
            console.error('Error fetching connections:', error);
        }
    }, [selectedBrand, currentUser]);

    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    // Handle date range change
    const handleDateRangeChange = (preset) => {
        setSelectedDateRange(preset);
        setDateRange(getDateRange(preset));
    };

    // Filter campaigns by platform
    const filteredCampaigns = campaigns.filter(c => c.platform === selectedPlatform);

    // Check if platform is connected
    const isPlatformConnected = platformConnections.some(
        conn => conn.platform === selectedPlatform && conn.status === 'active'
    );

    // Calculate aggregate stats for current platform
    const platformStats = filteredCampaigns.reduce((acc, campaign) => {
        const m = campaign.currentMetrics || {};
        return {
            totalCampaigns: acc.totalCampaigns + 1,
            activeCampaigns: acc.activeCampaigns + (m.status === 'ACTIVE' ? 1 : 0),
            totalSpend: acc.totalSpend + (m.spend || 0),
            totalRevenue: acc.totalRevenue + (m.revenue || 0),
            totalImpressions: acc.totalImpressions + (m.impressions || 0),
            totalClicks: acc.totalClicks + (m.clicks || 0)
        };
    }, {
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalSpend: 0,
        totalRevenue: 0,
        totalImpressions: 0,
        totalClicks: 0
    });

    const avgRoas = platformStats.totalSpend > 0
        ? platformStats.totalRevenue / platformStats.totalSpend
        : 0;

    // Handle analysis cache updates
    const updateCreativeAnalysisCache = (creativeId, analysis) => {
        setCreativeAnalysisCache(prev => {
            const updated = { ...prev, [creativeId]: analysis };
            try {
                localStorage.setItem(`campaign-creative-analysis-${selectedBrand.id}`, JSON.stringify({
                    data: updated,
                    timestamp: Date.now()
                }));
            } catch (e) {
                console.warn('Failed to save creative analysis cache:', e);
            }
            return updated;
        });
    };

    const updateCampaignAnalysisCache = (campaignId, analysis) => {
        setCampaignAnalysisCache(prev => {
            const updated = { ...prev, [campaignId]: analysis };
            try {
                localStorage.setItem(`campaign-analysis-${selectedBrand.id}`, JSON.stringify({
                    data: updated,
                    timestamp: Date.now()
                }));
            } catch (e) {
                console.warn('Failed to save campaign analysis cache:', e);
            }
            return updated;
        });
    };

    if (!selectedBrand) {
        return (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                <p className="text-secondary">Please select a brand to view campaign creatives.</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 'var(--space-6)'
            }}>
                <div>
                    <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: 'var(--space-2)' }}>
                        Campaign Creatives
                    </h1>
                    <p className="text-secondary">
                        Analyze and optimize creatives within your campaigns
                    </p>
                </div>

                {/* Date Range Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <Calendar size={18} className="text-secondary" />
                    <select
                        value={selectedDateRange}
                        onChange={(e) => handleDateRangeChange(e.target.value)}
                        className="form-select"
                        style={{
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text-primary)',
                            fontSize: 'var(--font-size-sm)',
                            minWidth: '180px'
                        }}
                    >
                        {Object.entries(DATE_RANGE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Platform Tabs */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-6)',
                borderBottom: '1px solid var(--color-border-light)',
                paddingBottom: 'var(--space-1)'
            }}>
                {PLATFORMS.map(platform => {
                    const isConnected = platformConnections.some(
                        c => c.platform === platform.id && c.status === 'active'
                    );
                    const isSelected = selectedPlatform === platform.id;
                    const campaignCount = campaigns.filter(c => c.platform === platform.id).length;

                    return (
                        <button
                            key={platform.id}
                            onClick={() => setSelectedPlatform(platform.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-3) var(--space-4)',
                                background: isSelected ? 'var(--color-primary)' : 'transparent',
                                color: isSelected ? 'white' : 'var(--color-text-primary)',
                                border: 'none',
                                borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                                cursor: 'pointer',
                                fontWeight: isSelected ? 600 : 400,
                                opacity: isConnected ? 1 : 0.5,
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            <span>{platform.icon}</span>
                            <span>{platform.label}</span>
                            {campaignCount > 0 && (
                                <span style={{
                                    background: isSelected ? 'rgba(255,255,255,0.2)' : 'var(--color-bg-tertiary)',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)'
                                }}>
                                    {campaignCount}
                                </span>
                            )}
                            {!isConnected && (
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    Not connected
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Stats Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)'
            }}>
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <Layers size={18} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Campaigns</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                        {platformStats.activeCampaigns}
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 400 }}>
                            {' '}/ {platformStats.totalCampaigns} total
                        </span>
                    </div>
                </div>

                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <DollarSign size={18} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Total Spend</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                        {formatCurrency(platformStats.totalSpend)}
                    </div>
                </div>

                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <TrendingUp size={18} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Total Revenue</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                        {formatCurrency(platformStats.totalRevenue)}
                    </div>
                </div>

                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                        <Target size={18} className="text-secondary" />
                        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Avg ROAS</span>
                    </div>
                    <div style={{
                        fontSize: 'var(--font-size-2xl)',
                        fontWeight: 700,
                        color: avgRoas >= 2 ? 'var(--color-success)' : avgRoas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                    }}>
                        {avgRoas.toFixed(2)}x
                    </div>
                </div>
            </div>

            {/* Campaign List */}
            <div className="card" style={{ overflow: 'hidden' }}>
                {/* List Header */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 1fr 80px',
                    gap: 'var(--space-4)',
                    padding: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    borderBottom: '1px solid var(--color-border-light)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)'
                }}>
                    <div></div>
                    <div>Campaign</div>
                    <div style={{ textAlign: 'right' }}>Spend</div>
                    <div style={{ textAlign: 'right' }}>Revenue</div>
                    <div style={{ textAlign: 'right' }}>ROAS</div>
                    <div style={{ textAlign: 'right' }}>Status</div>
                    <div></div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                        <RefreshCw size={24} className="spin text-secondary" />
                        <p className="text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                            Loading campaigns...
                        </p>
                    </div>
                )}

                {/* Not Connected State */}
                {!loading && !isPlatformConnected && (
                    <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                        <AlertTriangle size={48} className="text-warning" style={{ opacity: 0.5 }} />
                        <h3 style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                            {PLATFORMS.find(p => p.id === selectedPlatform)?.label} Not Connected
                        </h3>
                        <p className="text-secondary">
                            Connect your {PLATFORMS.find(p => p.id === selectedPlatform)?.label} Ads account
                            in the Campaign Monitoring page to see creatives.
                        </p>
                    </div>
                )}

                {/* Empty State */}
                {!loading && isPlatformConnected && filteredCampaigns.length === 0 && (
                    <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                        <BarChart3 size={48} className="text-secondary" style={{ opacity: 0.3 }} />
                        <h3 style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                            No Campaigns Found
                        </h3>
                        <p className="text-secondary">
                            No campaigns found for {PLATFORMS.find(p => p.id === selectedPlatform)?.label}.
                            Sync your campaigns from the Campaign Monitoring page.
                        </p>
                    </div>
                )}

                {/* Campaign Rows */}
                {!loading && filteredCampaigns.map(campaign => (
                    <CampaignCreativeRow
                        key={campaign.id}
                        campaign={campaign}
                        isExpanded={expandedCampaignId === campaign.id}
                        onToggle={() => setExpandedCampaignId(
                            expandedCampaignId === campaign.id ? null : campaign.id
                        )}
                        dateRange={dateRange}
                        userId={currentUser?.uid}
                        brandId={selectedBrand?.id}
                        creativeAnalysisCache={creativeAnalysisCache}
                        campaignAnalysisCache={campaignAnalysisCache}
                        onCreativeAnalysisUpdate={updateCreativeAnalysisCache}
                        onCampaignAnalysisUpdate={updateCampaignAnalysisCache}
                    />
                ))}
            </div>
        </div>
    );
};

export default CampaignCreatives;
